<template>
  <v-container style="padding-bottom:0px">
    <v-expansion-panels class="mt-4">
      <v-expansion-panel id="aabb-sms-panel">
        <v-expansion-panel-title>A-Affordable SMS Messages</v-expansion-panel-title>
        <v-expansion-panel-text>
          <div class="aabb-sms-container">

            <!-- Settings Section -->
            <div class="settings-section">
              <div class="settings-row">
                <v-text-field
                  v-model="emailRecipients"
                  label="Email Notification Recipients"
                  placeholder="comma-separated email addresses"
                  density="comfortable"
                  hide-details
                  class="recipients-field"
                ></v-text-field>
                <v-btn class="save-button" @click="saveConfig" :disabled="saving">
                  {{ saving ? 'Saving...' : 'Save' }}
                </v-btn>
              </div>
            </div>

            <!-- Controls Row -->
            <div class="controls-row">
              <v-btn class="refresh-button" @click="refreshMessages" :disabled="loading">
                {{ loading ? 'Loading...' : 'Refresh' }}
              </v-btn>
              <div class="controls-spacer"></div>
              <v-text-field
                v-model="searchFilter"
                placeholder="enter partial number or text to search"
                label="Search"
                density="compact"
                hide-details
                clearable
                class="search-field"
              ></v-text-field>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
              <p class="mt-2 loading-text">Loading SMS messages...</p>
            </div>

            <!-- Message List -->
            <div v-else class="messages-list">
              <div v-if="filteredMessages.length === 0" class="no-messages">
                <p>{{ messages.length === 0 ? 'No SMS messages found.' : 'No messages match your search.' }}</p>
              </div>

              <div
                v-for="(message, index) in filteredMessages"
                :key="message.messageSid"
                class="message-card"
                :style="{ backgroundColor: index % 2 === 0 ? '#faf8f0' : '#ffffff' }"
              >
                <!-- Single-line header: badge, from→to, timestamp -->
                <div class="message-header">
                  <span 
                    class="direction-badge"
                    :class="getDirection(message) === 'Inbound' ? 'inbound' : 'outbound'"
                  >
                    {{ getDirection(message) }}
                  </span>
                  <span class="phone-label">From:</span>
                  <a
                    class="phone-value phone-link"
                    :title="'Send SMS to ' + formatPhone(message.from)"
                    @click.prevent="onPhoneClick(message.from)"
                  >{{ formatPhone(message.from) }}</a>
                  <span class="phone-separator">→</span>
                  <span class="phone-label">To:</span>
                  <a
                    class="phone-value phone-link"
                    :title="'Send SMS to ' + formatPhone(message.to)"
                    @click.prevent="onPhoneClick(message.to)"
                  >{{ formatPhone(message.to) }}</a>
                  <span class="message-timestamp">{{ formatTimestamp(message.receivedTimestamp) }}</span>
                </div>

                <!-- Body -->
                <div class="message-body">{{ message.body }}</div>

                <!-- Thumbnails -->
                <div v-if="message.mediaUrls && message.mediaUrls.length > 0" class="message-thumbnails">
                  <a
                    v-for="(media, mIndex) in message.mediaUrls"
                    :key="mIndex"
                    :href="proxyMediaUrl(media.url)"
                    target="_blank"
                    class="thumbnail-link"
                  >
                    <img
                      :src="proxyMediaUrl(media.url)"
                      :alt="'Attachment ' + (mIndex + 1)"
                      class="thumbnail-img"
                    >
                  </a>
                </div>
              </div>
            </div>

          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Send SMS Dialog -->
    <v-dialog v-model="showComposeDialog" width="450">
      <v-card class="compose-dialog">
        <v-card-title class="compose-title">
          Send SMS
          <v-btn
            icon="mdi-close"
            variant="text"
            density="compact"
            class="compose-close"
            @click="closeCompose"
            :disabled="sending"
          ></v-btn>
        </v-card-title>

        <v-card-text class="compose-body">
          <!-- To field -->
          <v-text-field
            v-model="composeTo"
            v-maska="'###-###-####'"
            label="To"
            placeholder="###-###-####"
            density="comfortable"
            hide-details
            :disabled="sending"
            class="compose-to-field"
          ></v-text-field>

          <!-- Body textarea -->
          <v-textarea
            v-model="composeBodyText"
            label="Message"
            rows="5"
            no-resize
            density="comfortable"
            hide-details
            :disabled="sending"
            class="compose-body-field"
          ></v-textarea>

          <!-- Hidden file input -->
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="onFileSelected"
          >

          <!-- Attach row + Send -->
          <div class="compose-action-row">
            <v-btn
              variant="outlined"
              size="small"
              prepend-icon="mdi-paperclip"
              :disabled="sending || !!composeImage"
              @click="$refs.fileInput.click()"
            >
              Attach image
            </v-btn>

            <!-- Thumbnail preview -->
            <div v-if="composeImage" class="compose-thumbnail-row">
              <img :src="composeImagePreviewUrl" alt="preview" class="compose-thumbnail">
              <span class="compose-filename" :title="composeImage.name">{{ composeImage.name }}</span>
              <v-btn
                icon="mdi-close"
                variant="text"
                density="compact"
                size="x-small"
                :disabled="sending"
                @click="removeImage"
              ></v-btn>
            </div>

            <div class="compose-spacer"></div>

            <v-btn
              class="compose-send-button"
              :disabled="!canSend || sending"
              @click="sendMessage"
            >
              {{ sending ? 'Sending...' : 'Send' }}
            </v-btn>
          </div>

          <!-- Inline error -->
          <div v-if="composeError" class="compose-error">
            {{ composeError }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import moment from 'moment-timezone';
import { vMaska } from 'maska/vue';

const MAX_IMAGE_BYTES = 2.5 * 1024 * 1024; // 2.5 MB

export default {
  name: 'CompanyAccountAABBSms',

  directives: { maska: vMaska },

  data() {
    return {
      loading: false,
      saving: false,
      emailRecipients: '',
      searchFilter: '',
      messages: [],
      aabbPhoneNumber: '',

      // Compose dialog state
      showComposeDialog: false,
      composeTo: '',
      composeBodyText: '',
      composeImage: null,
      composeImagePreviewUrl: '',
      composeError: '',
      sending: false,

      // ============================ SSE state =============================
      eventSource: null,
      sseRefetchTimer: null,
    };
  },

  computed: {
    ...mapGetters(['getSelectedCompany']),

    companyNumber() {
      return this.getSelectedCompany?.companyNumber;
    },

    filteredMessages() {
      if (!this.searchFilter || this.searchFilter.trim() === '') {
        return this.messages;
      }

      const filter = this.searchFilter.toLowerCase().trim();
      return this.messages.filter(msg => {
        const from = (msg.from || '').toLowerCase();
        const to = (msg.to || '').toLowerCase();
        const body = (msg.body || '').toLowerCase();
        const fromPretty = this.formatPhone(msg.from).toLowerCase();
        const toPretty = this.formatPhone(msg.to).toLowerCase();

        return from.includes(filter) ||
               to.includes(filter) ||
               body.includes(filter) ||
               fromPretty.includes(filter) ||
               toPretty.includes(filter);
      });
    },

    // Phone is valid if it has exactly 10 digits
    isValidPhone() {
      const digits = (this.composeTo || '').replace(/\D/g, '');
      return digits.length === 10;
    },

    // Send enabled when: valid phone AND (body has content OR image attached)
    canSend() {
      const hasBody = (this.composeBodyText || '').trim().length > 0;
      const hasImage = !!this.composeImage;
      return this.isValidPhone && (hasBody || hasImage);
    }
  },

  methods: {
    async loadData() {
      this.loading = true;

      try {
        // Fetch config and messages in parallel
        const [configResponse, messagesResponse] = await Promise.all([
          axios.get('/portaldb/db/customDb/aabb/config'),
          axios.get('/portaldb/db/customDb/aabb/smsMessages')
        ]);

        // Config
        if (configResponse.data?.success && configResponse.data.data) {
          this.emailRecipients = configResponse.data.data.emailNotificationRecipients || '';
        }

        // Messages
        if (messagesResponse.data?.success) {
          this.messages = messagesResponse.data.data || [];
          this.aabbPhoneNumber = messagesResponse.data.aabbPhoneNumber || '';
        }
      } catch (error) {
        console.error('Error loading AABB SMS data:', error);
      } finally {
        this.loading = false;
      }
    },

    async saveConfig() {
      this.saving = true;

      try {
        await axios.put('/portaldb/db/customDb/aabb/config', {
          emailNotificationRecipients: this.emailRecipients
        });
        console.log('AABB config saved');
      } catch (error) {
        console.error('Error saving AABB config:', error);
      } finally {
        this.saving = false;
      }
    },

    async refreshMessages() {
      this.loading = true;

      try {
        const response = await axios.get('/portaldb/db/customDb/aabb/smsMessages');
        if (response.data?.success) {
          this.messages = response.data.data || [];
          this.aabbPhoneNumber = response.data.aabbPhoneNumber || '';
        }
      } catch (error) {
        console.error('Error refreshing AABB SMS messages:', error);
      } finally {
        this.loading = false;
      }
    },

    getDirection(message) {
      if (!this.aabbPhoneNumber) return '';
      if (message.to === this.aabbPhoneNumber) return 'Inbound';
      if (message.from === this.aabbPhoneNumber) return 'Outbound';
      return '';
    },

    formatPhone(phone) {
      if (!phone) return 'N/A';
      if (phone.startsWith('+1') && phone.length === 12) {
        return `(${phone.substring(2, 5)}) ${phone.substring(5, 8)}-${phone.substring(8)}`;
      }
      return phone;
    },

    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      const tz = this.getSelectedCompany?.timezone || 'America/Los_Angeles';
      return moment(timestamp).tz(tz).format('DD MMM h:mma, ddd');
    },

    proxyMediaUrl(url) {
      return `/portaldb/db/customDb/aabb/media?url=${encodeURIComponent(url)}`;
    },

    // ---- Compose dialog ----

    onPhoneClick(phoneNumber) {
      // Normalize to 10 digits, then pre-format with dashes
      const digits = (phoneNumber || '').replace(/\D/g, '');
      let tenDigits = digits;
      if (digits.length === 11 && digits.startsWith('1')) {
        tenDigits = digits.substring(1);
      }
      if (tenDigits.length === 10) {
        this.composeTo = `${tenDigits.substring(0, 3)}-${tenDigits.substring(3, 6)}-${tenDigits.substring(6)}`;
      } else {
        this.composeTo = tenDigits;
      }
      this.composeBodyText = '';
      this.composeImage = null;
      this.composeImagePreviewUrl = '';
      this.composeError = '';
      this.showComposeDialog = true;
    },

    closeCompose() {
      if (this.sending) return;
      this.showComposeDialog = false;
      this.resetComposeState();
    },

    resetComposeState() {
      this.composeTo = '';
      this.composeBodyText = '';
      if (this.composeImagePreviewUrl) {
        URL.revokeObjectURL(this.composeImagePreviewUrl);
      }
      this.composeImage = null;
      this.composeImagePreviewUrl = '';
      this.composeError = '';
    },

    onFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      // Reset the input value so picking the same file again still fires change
      event.target.value = '';

      if (!file) return;

      this.composeError = '';

      if (!file.type.startsWith('image/')) {
        this.composeError = 'Only image files are allowed.';
        return;
      }

      if (file.size > MAX_IMAGE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
        this.composeError = `Image is ${sizeMB} MB. Maximum size is 2.5 MB.`;
        return;
      }

      if (this.composeImagePreviewUrl) {
        URL.revokeObjectURL(this.composeImagePreviewUrl);
      }

      this.composeImage = file;
      this.composeImagePreviewUrl = URL.createObjectURL(file);
    },

    removeImage() {
      if (this.composeImagePreviewUrl) {
        URL.revokeObjectURL(this.composeImagePreviewUrl);
      }
      this.composeImage = null;
      this.composeImagePreviewUrl = '';
      this.composeError = '';
    },

    async sendMessage() {
      this.sending = true;
      this.composeError = '';

      try {
        const digits = (this.composeTo || '').replace(/\D/g, '');
        const formData = new FormData();
        formData.append('to', '+1' + digits);
        formData.append('body', this.composeBodyText || '');
        if (this.composeImage) {
          formData.append('image', this.composeImage);
        }

        const response = await axios.post(
          '/portaldb/db/customDb/aabb/sendSms',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data?.success) {
          // SSE will deliver the new message; just close the dialog.
          this.showComposeDialog = false;
          this.resetComposeState();
        } else {
          this.composeError = response.data?.message || 'Send failed.';
        }
      } catch (error) {
        // Pull message from response body when present, otherwise fall back to axios message
        const serverMsg = error.response?.data?.message;
        this.composeError = serverMsg || error.message || 'Send failed.';
        console.error('Error sending AABB SMS:', error);
      } finally {
        this.sending = false;
      }
    },

    // -------------------------------------------------------------
    // SSE — live updates
    // -------------------------------------------------------------

    setupSSE() {
      if (!this.companyNumber) return;
      this.teardownSSE();

      const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const url = `/portaldb/db/events/${uuid}/${this.companyNumber}`;
      console.log('[AABB SMS SSE] opening:', url);

      const es = new EventSource(url);

      es.addEventListener('aabbSmsMessages', () => {
        // We don't need to inspect the doc — any change to AABB SMS
        // means we should refetch the list. Debounce to coalesce
        // rapid consecutive events (e.g. a sync that upserts many docs).
        this._scheduleRefetch();
      });

      es.addEventListener('error', (err) => {
        console.warn('[AABB SMS SSE] error (auto-reconnect handles):', err);
      });

      this.eventSource = es;
    },

    teardownSSE() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      clearTimeout(this.sseRefetchTimer);
      this.sseRefetchTimer = null;
    },

    _scheduleRefetch() {
      clearTimeout(this.sseRefetchTimer);
      this.sseRefetchTimer = setTimeout(() => {
        this.refreshMessages();
      }, 500);
    }
  },

  async mounted() {
    await this.loadData();
    this.setupSSE();
  },

  beforeUnmount() {
    if (this.composeImagePreviewUrl) {
      URL.revokeObjectURL(this.composeImagePreviewUrl);
    }
    this.teardownSSE();
  }
};
</script>

<style scoped>
.aabb-sms-container {
  padding: 10px 0;
}

.settings-section {
  margin-bottom: 16px;
  padding: 16px;
  background-color: #fff;
  border: 1px solid #d2b48c;
  border-radius: 4px;
}

.settings-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.recipients-field {
  flex: 1;
}

.save-button {
  background-color: #d2b48c !important;
  flex-shrink: 0;
}

.controls-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.controls-spacer {
  flex: 1;
}

.refresh-button {
  background-color: #d2b48c !important;
  flex-shrink: 0;
}

.search-field {
  flex: 0 1 350px;
}

.loading-text {
  font-size: 14px;
  color: #666;
}

.messages-list {
  max-height: 600px;
  overflow-y: auto;
}

.no-messages {
  text-align: center;
  color: #666;
  font-style: italic;
  padding: 20px;
}

.message-card {
  border: 1px solid #e0d5c0;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 4px;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.direction-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 3px;
}

.direction-badge.inbound {
  background-color: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
}

.direction-badge.outbound {
  background-color: #e3f2fd;
  color: #1565c0;
  border: 1px solid #90caf9;
}

.message-timestamp {
  font-size: 13px;
  color: #888;
  margin-left: auto;
}

.phone-label {
  font-weight: 600;
  color: #666;
  font-size: 13px;
}

.phone-value {
  font-family: monospace;
  font-size: 13px;
}

.phone-separator {
  color: #bbb;
}

.phone-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  border-radius: 2px;
}

.phone-link:hover {
  color: #8a6a3d;
  text-decoration: underline;
}

.phone-link:focus-visible {
  outline: 2px solid #8a6a3d;
  outline-offset: 2px;
  text-decoration: underline;
}

.message-body {
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  padding: 6px 8px;
  background-color: #f8f0e3;
  border: 1px solid #e0d5c0;
  border-radius: 4px;
}

.message-thumbnails {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.thumbnail-link {
  display: block;
}

.thumbnail-img {
  width: 120px;
  height: auto;
  border: 1px solid #d2b48c;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.thumbnail-img:hover {
  transform: scale(1.05);
}

/* ----- Compose dialog ----- */

.compose-dialog {
  background-color: #faf8f0 !important;
}

.compose-title {
  background-color: #d2b48c !important;
  color: #ffffff !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  padding: 12px 16px !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.compose-close {
  color: #ffffff !important;
}

.compose-body {
  padding: 16px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.compose-to-field :deep(input) {
  font-family: monospace;
}

.compose-body-field {
  width: 100%;
}

.compose-action-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.compose-spacer {
  flex: 1;
}

.compose-thumbnail-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
}

.compose-thumbnail {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border: 1px solid #d2b48c;
  border-radius: 3px;
}

.compose-filename {
  font-size: 12px;
  color: #555;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compose-send-button {
  background-color: #d2b48c !important;
  color: #ffffff !important;
}

.compose-error {
  color: #b00020;
  font-size: 13px;
  margin-top: 4px;
}

/* ----- Existing accordion styles ----- */

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #ffffff !important;
  background-color: #d2b48c !important;
  border: 1px solid #d2b48c !important;
  min-height: 64px !important;
  padding: 16px !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
  color: #ffffff !important;
}

:deep(.v-expansion-panel-text) {
  background-color: #f5f5dc !important;
  border: 1px solid #d2b48c !important;
  border-top: none !important;
}

#aabb-sms-panel {
  margin-bottom: 8px;
}

@media (max-width: 600px) {
  .settings-row {
    flex-direction: column;
    align-items: stretch;
  }

  .controls-row {
    flex-direction: column;
    align-items: stretch;
  }

  .controls-spacer {
    display: none;
  }

  .search-field {
    flex: 1;
  }

  .message-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .message-timestamp {
    margin-left: 0;
  }
}
</style>
